import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

/* =========================
   INTERPRETATION HELPERS
========================= */

// Convert numeric ratings into founder-safe language
function interpretScore(avg) {
  if (avg >= 4) return { label: "Strongly supported", color: "#16a34a" };
  if (avg >= 2.5) return { label: "Moderately supported", color: "#f59e0b" };
  return { label: "Needs stronger evidence", color: "#dc2626" };
}

// Safe text explanation
function explanation(label) {
  switch (label) {
    case "Strongly supported":
      return "Evaluators found the available evidence sufficient to support this claim.";
    case "Moderately supported":
      return "Some evidence supports this claim, but additional verification would improve credibility.";
    default:
      return "Evaluators could not independently verify this claim using the current evidence.";
  }
}

function CredibilityFeedback() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [evaluations, setEvaluations] = useState([]);

  /* =========================
     LOAD CLAIMS
  ========================= */
  useEffect(() => {
    if (!user) return;

    const loadClaims = async () => {
      const q = query(
        collection(db, "claims"),
        where("userId", "==", user.uid)
      );
      const snap = await getDocs(q);
      setClaims(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    loadClaims();
  }, [user]);

  /* =========================
     LOAD EVALUATIONS
  ========================= */
  useEffect(() => {
    if (!user) return;

    const loadEvaluations = async () => {
      const q = query(
        collection(db, "evaluations"),
        where("founderId", "==", user.uid),
        where("status", "==", "submitted")
      );
      const snap = await getDocs(q);
      setEvaluations(snap.docs.map((d) => d.data()));
      setLoading(false);
    };

    loadEvaluations();
  }, [user]);

  if (loading) {
    return <p>Loading credibility feedback…</p>;
  }

  /* =========================
     AGGREGATE CLAIM FEEDBACK
  ========================= */
  const claimFeedback = claims.map((claim) => {
    let total = 0;
    let count = 0;

    evaluations.forEach((e) => {
      const rating = e.claimRatings?.[claim.id]?.rating;
      if (typeof rating === "number") {
        total += rating;
        count++;
      }
    });

    const avg = count > 0 ? total / count : null;
    const interpreted = avg ? interpretScore(avg) : null;

    return {
      ...claim,
      avg,
      count,
      interpreted,
    };
  });

  const strong = claimFeedback.filter(
    (c) => c.interpreted?.label === "Strongly supported"
  ).length;

  const weak = claimFeedback.filter(
    (c) => c.interpreted?.label === "Needs stronger evidence"
  ).length;

  /* =========================
     UI
  ========================= */
  return (
    <div>
      {/* ================= HEADER ================= */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{ marginBottom: 6 }}
          title="This section reflects how credible your claims appear to independent evaluators."
        >
          Credibility Feedback
        </h1>
        <p
          style={{ color: "#64748b", maxWidth: 720, lineHeight: 1.6 }}
          title="This is not about opinions. It’s about evidence-backed trust."
        >
          See how evaluators perceive your claims based on the strength of the
          evidence you’ve shared. Use this feedback to strengthen trust and
          clarity in your startup narrative.
        </p>
      </div>

      {/* ================= SUMMARY CARDS ================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 18,
          marginBottom: 40,
        }}
      >
        <StatCard
          label="Total Claims Reviewed"
          value={claimFeedback.length}
          tooltip="Number of claims that evaluators have assessed."
        />
        <StatCard
          label="Strongly Supported"
          value={strong}
          color="#16a34a"
          tooltip="Claims with strong, convincing evidence."
        />
        <StatCard
          label="Needs Stronger Evidence"
          value={weak}
          color="#dc2626"
          tooltip="Claims where evidence was insufficient or unclear."
        />
        <StatCard
          label="Total Evaluator Reviews"
          value={evaluations.length}
          tooltip="Independent evaluations submitted for your startup."
        />
      </div>

      {/* ================= CLAIM FEEDBACK ================= */}
      {claimFeedback.length === 0 && (
        <div
          className="card"
          style={{
            padding: 28,
            textAlign: "center",
            color: "#64748b",
          }}
          title="Once evaluators submit reviews, insights will appear here."
        >
          No credibility feedback yet. Once evaluators review your claims,
          insights will appear here.
        </div>
      )}

      {claimFeedback.map((claim) => (
        <div
          key={claim.id}
          className="card"
          style={{
            padding: 24,
            marginBottom: 24,
            borderLeft: `5px solid ${
              claim.interpreted?.color || "#e2e8f0"
            }`,
          }}
          title="Each claim is assessed independently by evaluators."
        >
          <div
            style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}
            title="This is the exact claim evaluators reviewed."
          >
            {claim.text}
          </div>

          {!claim.interpreted ? (
            <div
              style={{ fontSize: 14, color: "#94a3b8" }}
              title="This claim has not yet been rated by evaluators."
            >
              This claim hasn’t been evaluated yet.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "inline-block",
                  marginBottom: 12,
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: `${claim.interpreted.color}15`,
                  color: claim.interpreted.color,
                  fontSize: 13,
                  fontWeight: 600,
                }}
                title="This reflects evaluator consensus, not AI judgment."
              >
                {claim.interpreted.label}
              </div>

              <p
                style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}
                title="Explanation based on evaluator ratings."
              >
                {explanation(claim.interpreted.label)}
              </p>

              <p
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  marginTop: 12,
                }}
                title="More reviews usually mean higher confidence."
              >
                Based on {claim.count} evaluator review
                {claim.count > 1 ? "s" : ""}.
              </p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/* =========================
   UI COMPONENTS
========================= */

const StatCard = ({ label, value, color = "#2563eb", tooltip }) => (
  <div
    className="card"
    style={{
      padding: 20,
      borderTop: `4px solid ${color}`,
    }}
    title={tooltip}
  >
    <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: 30, fontWeight: 700 }}>{value}</div>
  </div>
);

export default CredibilityFeedback;
